import React, { useEffect, useState, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Check, Loader2, AlertCircle } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PaymentSuccess() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('checking');
  const [paymentData, setPaymentData] = useState(null);
  const sessionId = searchParams.get('session_id');

  const pollStatus = useCallback(async (attempts = 0) => {
    if (!sessionId) {
      setStatus('error');
      return;
    }
    const maxAttempts = 5;
    if (attempts >= maxAttempts) {
      setStatus('timeout');
      return;
    }
    try {
      const res = await axios.get(`${API}/checkout/status/${sessionId}`);
      const data = res.data;
      if (data.payment_status === 'paid') {
        setPaymentData(data);
        setStatus('success');
        return;
      } else if (data.status === 'expired') {
        setStatus('expired');
        return;
      }
      setTimeout(() => pollStatus(attempts + 1), 2000);
    } catch {
      if (attempts < maxAttempts - 1) {
        setTimeout(() => pollStatus(attempts + 1), 2000);
      } else {
        setStatus('error');
      }
    }
  }, [sessionId]);

  useEffect(() => {
    pollStatus();
  }, [pollStatus]);

  return (
    <div className="min-h-screen bg-black flex items-center justify-center px-6" data-testid="payment-success-page">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="text-center max-w-md"
      >
        {status === 'checking' && (
          <div data-testid="payment-checking">
            <Loader2 size={40} className="text-white/40 animate-spin mx-auto mb-6" />
            <h2 className="font-heading text-2xl mb-3">VERIFYING PAYMENT</h2>
            <p className="text-white/30 text-sm font-mono">Processing your transaction...</p>
          </div>
        )}

        {status === 'success' && (
          <div data-testid="payment-confirmed">
            <div className="w-20 h-20 border border-white/20 flex items-center justify-center mx-auto mb-8">
              <Check size={36} className="text-white" />
            </div>
            <h2 className="font-heading text-3xl mb-4">PAYMENT CONFIRMED</h2>
            <p className="text-white/40 text-sm mb-2">Your deposit has been received.</p>
            {paymentData && (
              <p className="font-mono text-xs text-white/20 mb-8">
                Amount: ${(paymentData.amount_total / 100).toFixed(2)} {paymentData.currency?.toUpperCase()}
              </p>
            )}
            <button onClick={() => navigate('/')} className="btn-primary text-xs" data-testid="back-home-button">
              Return Home
            </button>
          </div>
        )}

        {(status === 'error' || status === 'timeout' || status === 'expired') && (
          <div data-testid="payment-error">
            <AlertCircle size={40} className="text-white/30 mx-auto mb-6" />
            <h2 className="font-heading text-2xl mb-3">
              {status === 'expired' ? 'SESSION EXPIRED' : 'VERIFICATION ISSUE'}
            </h2>
            <p className="text-white/30 text-sm mb-8">
              {status === 'expired'
                ? 'This payment session has expired. Please try again.'
                : 'We could not verify your payment. Please check your email for confirmation.'}
            </p>
            <button onClick={() => navigate('/')} className="btn-outline text-xs" data-testid="back-home-error-button">
              Return Home
            </button>
          </div>
        )}
      </motion.div>
    </div>
  );
}
